import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { EntrarComCodigo } from '@/components/auth/EntrarComCodigo';
import { ForgotPasswordModal } from '@/components/auth/ForgotPasswordModal';
import { Blueprint, Button, Field, Input, Radio } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { LinkDoCadastro } from '@/components/auth/LinkDoCadastro';
import { AuthError, login } from '@/lib/auth';
import { PERITO_HOME_ROUTE, resolveHomeRoute } from '@/lib/auth/homeRoute';
import { detectCapsLock } from '@/lib/keyboard';
import { getPerfil } from '@/lib/perfil';
import { useSessionStore } from '@/stores/session';

function mensagemDoErro(err: unknown): string {
  const reason = err instanceof AuthError ? err.reason : 'unknown';
  if (reason === 'invalid_credentials') return ptBR.errors.invalidCredentials;
  if (reason === 'too_many_attempts') return ptBR.errors.tooManyAttempts;
  if (reason === 'network') return ptBR.errors.networkOffline;
  return ptBR.errors.generic;
}

interface FormularioDaSenhaProps {
  email: string;
  onEmail: (email: string) => void;
  onEntrou: (sessao: string) => Promise<void>;
  /** O que vem depois do botão de entrar (o link do código, no endereço do QR). */
  rodape?: ReactNode;
}

/** O login com senha: e-mail, senha, "manter conectado", esqueci a senha e a recusa. */
function FormularioDaSenha({ email, onEmail, onEntrou, rodape }: FormularioDaSenhaProps) {
  const apiClient = useApiClient();
  const emailRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const resp = await login(apiClient, email, password);
      await onEntrou(resp.access_token);
    } catch (err) {
      setErrorMessage(mensagemDoErro(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        aria-describedby={errorMessage ? 'login-error' : undefined}
        className="mt-[26px] flex flex-col gap-4"
      >
        <Field label={ptBR.login.emailLabel} htmlFor="login-email-input">
          <Input
            ref={emailRef}
            id="login-email-input"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            placeholder={ptBR.login.emailPlaceholder}
            disabled={submitting}
            data-testid="login-email"
          />
        </Field>

        <Field label={ptBR.login.passwordLabel} htmlFor="login-password-input">
          <div className="relative flex">
            <Input
              id="login-password-input"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={(e) => setCapsLock(detectCapsLock(e))}
              onKeyDown={(e) => setCapsLock(detectCapsLock(e))}
              onBlur={() => setCapsLock(false)}
              placeholder={ptBR.login.passwordPlaceholder}
              disabled={submitting}
              aria-describedby={capsLock ? 'login-caps' : undefined}
              className="pr-10"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? ptBR.login.passwordHide : ptBR.login.passwordShow}
              aria-pressed={showPassword}
              data-testid="login-password-toggle"
              className="absolute inset-y-0 right-2 grid w-7 place-items-center text-fg-dim transition-colors duration-fast ease-sc hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
              tabIndex={0}
            >
              {showPassword ? (
                <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
              ) : (
                <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
              )}
            </button>
          </div>
          {capsLock ? (
            <p
              id="login-caps"
              data-testid="login-caps-warning"
              role="status"
              className="mt-1 text-caption text-warning-700"
            >
              {ptBR.login.capsLockOn}
            </p>
          ) : null}
        </Field>

        <div className="flex items-center justify-between gap-3 text-body-sm">
          {/*
          O "manter conectado" da prancha 1A é o `.radio` do pacote. Continua
          sendo uma escolha de duas vias (marcado/desmarcado) — o que muda é a
          forma do controle, não a semântica do estado, que segue booleana.
        */}
          <Radio
            name="login-remember"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            data-testid="login-remember"
            wrapperClassName="text-body-sm"
          >
            {ptBR.login.rememberMe}
          </Radio>
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            data-testid="login-forgot-cta"
            className="shrink-0 text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {ptBR.login.forgotPassword}
          </button>
        </div>

        {errorMessage ? (
          <p
            id="login-error"
            role="alert"
            data-testid="login-error"
            className="border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          >
            {errorMessage}
          </p>
        ) : null}

        <Button
          type="submit"
          size="block"
          disabled={submitting}
          className="mt-1.5 tracking-[0.04em]"
          data-testid="login-submit"
        >
          {submitting ? ptBR.login.submitting : ptBR.login.submit}
        </Button>
        {rodape}
      </form>
      <ForgotPasswordModal open={forgotOpen} onOpenChange={setForgotOpen} initialEmail={email} />
    </>
  );
}

export function LoginPage() {
  const apiClient = useApiClient();
  const setToken = useSessionStore((s) => s.setToken);
  const navigate = useNavigate();
  const location = useLocation();
  // Aviso pós-reset de senha (E35.F2.C03): a ResetPasswordPage redireciona para
  // cá com `state.passwordReset` após trocar a senha.
  const passwordReset = (location.state as { passwordReset?: boolean } | null)?.passwordReset;
  // Rota que o AuthGuard preservou antes de mandar o usuário para cá — tem
  // prioridade sobre a casa do ambiente (os guards de área corrigem depois,
  // se o destino não pertencer ao usuário).
  const fromRoute = (location.state as { from?: string } | null)?.from;
  // Ajuste do owner (03/11, ADR-0149): o AuthGuard marca `modo: 'codigo'` quando o endereço é o do
  // caso impresso no QR do exame. A entrada abre pelo código enviado ao e-mail, e a senha fica a um
  // clique; fora desse endereço, o login segue só com a senha.
  const veioDoQr = (location.state as { modo?: string } | null)?.modo === 'codigo';
  const [usarCodigo, setUsarCodigo] = useState(veioDoQr);
  const [email, setEmail] = useState('');

  async function seguir(sessao: string): Promise<void> {
    setToken(sessao);
    // E50.F2: o destino pós-login depende do ambiente do usuário
    // (interno → /casos; escritorio/banco → /portal/casos). Se o perfil não
    // resolver, cai na área do perito — o PortalGuard corrige na navegação.
    let destination = fromRoute ?? PERITO_HOME_ROUTE;
    if (!fromRoute) {
      try {
        destination = resolveHomeRoute(await getPerfil(apiClient));
      } catch {
        destination = PERITO_HOME_ROUTE;
      }
    }
    navigate(destination, { replace: true });
  }

  const linkDoCodigo = veioDoQr ? (
    <button
      type="button"
      onClick={() => setUsarCodigo(true)}
      data-testid="login-usar-codigo"
      className="self-start text-body-sm text-accent-700 underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-accent"
    >
      {ptBR.login.codigo.usarCodigo}
    </button>
  ) : null;

  return (
    <Blueprint
      as="section"
      data-testid="login-page"
      aria-labelledby="login-title"
      className="bg-bg px-8 py-[34px]"
    >
      <p className="mb-2.5 font-titulo text-rotulo uppercase text-fg-dim">
        {ptBR.authShell.accessKicker}
      </p>
      <h1 id="login-title" className="font-display text-[32px] leading-none text-fg">
        {ptBR.login.pageTitle}
      </h1>
      <p className="mt-1.5 text-body-sm text-fg-muted">
        {usarCodigo ? ptBR.login.codigo.intro : ptBR.login.intro}
      </p>

      {passwordReset ? (
        <p
          role="status"
          data-testid="login-reset-success"
          className="mt-4 border border-success-500 bg-success-50 px-3 py-2 text-body-sm text-success-700"
        >
          {ptBR.login.resetSuccess}
        </p>
      ) : null}

      {usarCodigo ? (
        <EntrarComCodigo
          onEntrou={seguir}
          onUsarSenha={() => setUsarCodigo(false)}
          emailInicial={email}
        />
      ) : (
        <FormularioDaSenha
          email={email}
          onEmail={setEmail}
          onEntrou={seguir}
          rodape={linkDoCodigo}
        />
      )}

      <div className="mt-[26px] flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4 text-[12.5px] text-fg-dim">
        <LinkDoCadastro />
        <span data-testid="login-footer" className="font-mono text-[11.5px]">
          {ptBR.login.footerVersion} · {ptBR.login.footerLgpd} · {ptBR.login.footerSupport}
        </span>
      </div>
    </Blueprint>
  );
}
